/**
 * API Error Types - Structured error handling for API calls
 *
 * These types provide exhaustive error handling for all API scenarios.
 * Use discriminated unions to enable type-safe error handling.
 */

/**
 * Base API error type - discriminated union of all possible API errors
 */
export type ApiError =
  | NetworkError
  | UnauthorizedError
  | ForbiddenError
  | NotFoundError
  | ConflictError
  | ValidationError
  | RateLimitError
  | ServerError
  | TimeoutError
  | ParseError;

/**
 * Network connectivity error (fetch failed, no internet, CORS, etc.)
 */
interface NetworkError {
  type: 'network';
  message: string;
  cause?: unknown;
}

/**
 * Authentication required (401)
 */
interface UnauthorizedError {
  type: 'unauthorized';
  status: 401;
  message: string;
}

/**
 * Insufficient permissions (403)
 */
export interface ForbiddenError {
  type: 'forbidden';
  status: 403;
  message: string;
  requiredPermission?: string;
}

/**
 * Resource not found (404)
 */
export interface NotFoundError {
  type: 'not_found';
  status: 404;
  resource: string;
  message: string;
}

/**
 * Resource already exists (409)
 */
export interface ConflictError {
  type: 'conflict';
  status: 409;
  message: string;
  code?: string;
  resource?: string;
}

/**
 * Request validation failed. The go-backend maps every VALIDATION_FAILED
 * APIError to HTTP 400 (see api_errors.go HTTPStatusCode), while 422 is kept
 * for compatibility with the (historical) 422 convention.
 */
export interface ValidationError {
  type: 'validation';
  status: 400 | 422;
  message: string;
  fields?: Record<string, string[]>;
}

/**
 * Rate limit exceeded (429)
 */
interface RateLimitError {
  type: 'rate_limit';
  status: 429;
  message: string;
  retryAfter?: number; // seconds
}

/**
 * Server error (500+)
 */
interface ServerError {
  type: 'server';
  status: number;
  message: string;
  code?: string;
}

/**
 * Request timeout
 */
interface TimeoutError {
  type: 'timeout';
  message: string;
  timeoutMs: number;
}

/**
 * JSON parse error or invalid response format
 */
interface ParseError {
  type: 'parse';
  message: string;
  cause?: unknown;
}

/**
 * Type guards for error types
 */

export function isForbiddenError(error: ApiError): error is ForbiddenError {
  return error.type === 'forbidden';
}

export function isNotFoundError(error: ApiError): error is NotFoundError {
  return error.type === 'not_found';
}

export function isConflictError(error: ApiError): error is ConflictError {
  return error.type === 'conflict';
}

export function isValidationError(error: ApiError): error is ValidationError {
  return error.type === 'validation';
}

/**
 * Get user-friendly error message
 */
/**
 * Error body structure from API responses
 */
interface ErrorBody {
  message?: string;
  error?: string | unknown;
  details?: {
    reason?: string;
  };
  requiredPermission?: string;
  resource?: string;
  fields?: Record<string, string[]>;
  retryAfter?: number;
  code?: string;
}

/**
 * Create error from HTTP response
 */
// The backend returns {"error":{"code","message","fields"?},"requestId"} for
// every version. Flatten it so the rest of this module keeps field access.
function normalizeErrorBody(raw: unknown): Partial<ErrorBody> {
  if (!raw || typeof raw !== 'object') return {};
  const body = raw as Record<string, unknown>;
  const nested = body.error;
  if (nested && typeof nested === 'object') {
    const inner = nested as { code?: unknown; message?: unknown; fields?: unknown };
    return {
      message: typeof inner.message === 'string' ? inner.message : undefined,
      code: typeof inner.code === 'string' ? inner.code : undefined,
      fields: (inner.fields as ErrorBody['fields']) ?? undefined,
      requiredPermission:
        typeof body.requiredPermission === 'string' ? body.requiredPermission : undefined,
      retryAfter: typeof body.retryAfter === 'number' ? body.retryAfter : undefined,
    };
  }
  return body as Partial<ErrorBody>;
}

export async function fromHttpResponse(response: globalThis.Response): Promise<ApiError> {
  const status = response.status;

  // Try to parse error body
  let errorBody: Partial<ErrorBody>;
  try {
    // Check if response has content before parsing
    const contentLength = response.headers.get('content-length');
    const contentType = response.headers.get('content-type');

    // Handle empty responses
    if (contentLength === '0' || status === 204) {
      errorBody = { message: response.statusText };
    } else if (contentType && contentType.includes('application/json')) {
      const text = await response.text();
      if (text && text.trim() !== '') {
        errorBody = normalizeErrorBody(JSON.parse(text));
      } else {
        errorBody = { message: response.statusText };
      }
    } else {
      // Non-JSON response, try to read as text
      const text = await response.text();
      errorBody = { message: text || response.statusText };
    }
  } catch {
    // If parsing fails, use status text
    errorBody = { message: response.statusText };
  }

  let message = response.statusText;

  // For validation errors, check if there's a more specific reason in details
  if (errorBody.details?.reason) {
    message = errorBody.details.reason;
  } else if (errorBody.error) {
    message =
      typeof errorBody.error === 'string' ? errorBody.error : JSON.stringify(errorBody.error);
  } else if (errorBody.message) {
    message =
      typeof errorBody.message === 'string' ? errorBody.message : JSON.stringify(errorBody.message);
  }

  // Map status codes to error types
  switch (status) {
    // 400 from the go-backend is a client-side validation failure
    // (VALIDATION_FAILED), not a server fault. Classifying it as 'server'
    // made forms fall into the generic error/retry branch instead of showing
    // the real validation reason (issue #707).
    case 400:
      return {
        type: 'validation',
        status: 400,
        message,
        fields: errorBody.fields,
      };
    case 401:
      return {
        type: 'unauthorized',
        status: 401,
        message,
      };
    case 403:
      return {
        type: 'forbidden',
        status: 403,
        message,
        requiredPermission: errorBody.requiredPermission,
      };
    case 404:
      return {
        type: 'not_found',
        status: 404,
        resource: errorBody.resource || 'Resource',
        message,
      };
    case 409:
      return {
        type: 'conflict',
        status: 409,
        message,
        code: errorBody.code,
        resource: errorBody.resource,
      };
    case 422:
      return {
        type: 'validation',
        status: 422,
        message,
        fields: errorBody.fields,
      };
    case 429:
      return {
        type: 'rate_limit',
        status: 429,
        message,
        retryAfter:
          errorBody.retryAfter || parseInt(response.headers.get('Retry-After') || '0', 10),
      };
    default:
      if (status >= 500) {
        return {
          type: 'server',
          status,
          message,
          code: errorBody.code,
        };
      }
      // Fallback for other 4xx errors
      return {
        type: 'server',
        status,
        message,
      };
  }
}
